// src/services/directSitePurchaseService.js
// Priority 4 — Direct Site Purchase: two-stage GRN
//   Stage 1: Site Engineer confirms received quantity (confirmSiteReceipt)
//   Stage 2: Manager/SM/Owner enters rate + finalizes (finalizeSitePurchaseGRN)
//
// D-295 (08 Oct 2026) — finalize fixes (F9):
//   - PO items are MERGED at finalize, never replaced. Every field saved at
//     PO creation (base_rate, discount, GST, freight share, landed cost,
//     line_id, ordered_qty) is kept; finalize only adds what was received.
//     Same pattern as the normal GRN (ReceiveMaterial.js: { ...pItem, ... }).
//   - Short / over delivery recorded with the SAME field names as the normal
//     GRN (purchaseReceiptService.js). A reason is mandatory when short.
//   - "finalizing" claim: ONE transaction checks the PO is site_confirmed and
//     moves it to "finalizing" BEFORE any stock or money is written, so two
//     managers (or a double click) can never finalize the same PO twice
//     (CORE_RULES §69). Full atomicity of stock + receipt + bill is D-292.
//   - Actual freight (reference only).
//   - D-291 P3d-4: finalize writes NO vendor payable and takes no invoice
//     no. / date any more. The receipt is saved "awaiting_bill"; the vendor's
//     bill (with its invoice no. and the duplicate check) is entered later in
//     Awaiting Bills (purchaseBillService.js), which creates the payable.
//   - confirmSiteReceipt: check + write in one transaction (§69) and sets
//     updated_by / updated_by_name for the audit trail.

import {
  doc, updateDoc, addDoc, setDoc, collection, serverTimestamp, runTransaction,
} from "firebase/firestore";
import { db } from "../firebase";
import { receiveGRNAtSite } from "./inventoryService";
import { applyDirectSiteIssueToStages } from "../pages/projects/projectMaterialService";

/* ==================================================
   STAGE 1 — SITE ENGINEER CONFIRMS RECEIVED QTY
   No inventory movement, no financial entry — a plain ground-truth
   observation, same pattern as Subcontractor Work's field-log stage
   (D-273): the log itself isn't yet financially actioned.
================================================== */
export const confirmSiteReceipt = async ({
  po_id,
  company_id,
  confirmed_items,   // [{ line_id, item_id, item_code, item_name, unit, ordered_qty, confirmed_qty }]
  confirmed_by,
  confirmed_by_name = "",
}) => {
  try {
    if (!po_id)        throw new Error("po_id required");
    if (!company_id)   throw new Error("company_id required");
    if (!confirmed_by) throw new Error("confirmed_by required");
    if (!Array.isArray(confirmed_items) || confirmed_items.length === 0) {
      throw new Error("At least one confirmed item required");
    }

    const poRef = doc(db, "purchase_orders", po_id);

    // D-295 (R4): check and write inside ONE transaction (CORE_RULES §69),
    // so two confirms sent at the same moment can never both pass the check.
    await runTransaction(db, async (tx) => {
      const poSnap = await tx.get(poRef);
      if (!poSnap.exists()) throw new Error("PO not found");
      const po = poSnap.data();

      if (po.company_id !== company_id) throw new Error("Unauthorized company access");
      if (po.purchase_mode !== "direct_site_purchase") {
        throw new Error("This PO is not a Direct Site Purchase");
      }
      if (po.po_status !== "ordered") {
        throw new Error(`Cannot confirm receipt — PO is "${po.po_status}". Must be Ordered first (Manager/Owner approves, then marks Ordered).`);
      }
      // D-295: only a PO still waiting for confirmation may be confirmed. The
      // old check refused only "site_confirmed" / locked, so a PO in the new
      // "finalizing" state would have let the Site Engineer overwrite the
      // quantities a Manager was finalizing at that moment.
      if (po.grn_locked || po.site_confirmation_status !== "pending_site_confirmation") {
        throw new Error(po.grn_locked
          ? "Already finalized — cannot re-confirm"
          : "Already confirmed — cannot re-confirm");
      }

      tx.update(poRef, {
        site_confirmed_items:     confirmed_items,
        site_confirmation_status: "site_confirmed",
        site_confirmed_by:        confirmed_by,
        site_confirmed_by_name:   confirmed_by_name,
        site_confirmed_at:        serverTimestamp(),
        updated_at:               serverTimestamp(),
        // D-295 (R3): audit trail — who last changed this PO.
        updated_by:               confirmed_by,
        updated_by_name:          confirmed_by_name,
      });
    });

    return { success: true };

  } catch (error) {
    console.error("confirmSiteReceipt error:", error);
    throw error;
  }
};

/* ==================================================
   LINE MATCHING — shared by the finalize screen and the service
   WHY: the Site Engineer's confirmed items must be matched back to the
   exact PO line they belong to, so the merge keeps that line's own
   breakdown. The screen uses the SAME functions to show quantities and
   key its rate inputs, so screen and service can never disagree.
================================================== */

// One key per PO line. createPurchaseOrder() writes line_id on every line;
// lines added later through Amend PO may not have one, so item_id is the
// fallback.
export const lineKeyOf = (line = {}) => String(line.line_id || line.item_id || "");

// Returns every ACTIVE (not cancelled) PO line with the quantity the Site
// Engineer confirmed for it (0 if not confirmed). Refuses — never guesses —
// when a line has no key, two lines share a key, or a confirmed item does
// not match exactly one line.
export const matchConfirmedToLines = (po = {}) => {
  const lines = (po.items || [])
    .map((line, index) => ({ line, index, key: lineKeyOf(line) }))
    .filter(x => !x.line._cancelled);

  const seen = new Set();
  for (const x of lines) {
    if (!x.key) {
      throw new Error(`PO line "${x.line.item_name || "?"}" has no item id — contact admin before finalizing.`);
    }
    if (seen.has(x.key)) {
      throw new Error(`Two PO lines share the same item ("${x.line.item_name}") — contact admin before finalizing.`);
    }
    seen.add(x.key);
  }

  const receivedByKey = {};
  for (const c of (po.site_confirmed_items || [])) {
    const matches = c.line_id
      ? lines.filter(x => x.line.line_id === c.line_id)
      : lines.filter(x => String(x.line.item_id || "") === String(c.item_id || ""));
    if (matches.length !== 1) {
      throw new Error(`Confirmed item "${c.item_name || c.item_id}" does not match exactly one PO line — contact admin before finalizing.`);
    }
    const key = matches[0].key;
    receivedByKey[key] = (receivedByKey[key] || 0) + Number(c.confirmed_qty || 0);
  }

  return lines.map(x => ({ ...x, received_qty: Number(receivedByKey[x.key] || 0) }));
};

// Same rule as ReceiveMaterial.js getItemGRNStatus().
const getItemGRNStatus = (received, ordered) => {
  if (ordered === 0)        return "over";
  if (received < ordered)   return "short";
  if (received === ordered) return "exact";
  return "over";
};

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

// Pure — builds everything finalize will write, or throws before anything
// is written. Called inside the claim transaction.
const buildFinalizePlan = (po, rates, reason) => {
  const matched = matchConfirmedToLines(po);
  const byIndex = new Map(matched.map(m => [m.index, m]));

  if (!matched.some(m => m.received_qty > 0)) {
    throw new Error("Nothing was confirmed as received — cannot finalize.");
  }

  let grandTotal = 0;
  let hasShort = false, hasOver = false;
  let totalOverQty = 0, totalCancelledQty = 0;
  const stockLines = [];

  const mergedItems = (po.items || []).map((line, index) => {
    const m = byIndex.get(index);
    if (!m) return line; // cancelled line — kept exactly as it was

    const received = m.received_qty;
    const ordered  = Number(line.ordered_qty ?? line.qty ?? 0);
    const entered  = Number(rates[m.key] || 0);

    if (received > 0 && !line.is_custom_item && !(entered > 0)) {
      throw new Error(`Rate missing for "${line.item_name}" — rate required for every received item`);
    }

    // Rate actually billed for this receipt. The PO's original rate stays in
    // base_rate (saved at creation), so nothing about the PO is lost.
    const rate      = received > 0 ? entered : Number(line.rate || 0);
    const cancelled = Math.max(ordered - received, 0);
    const over      = Math.max(received - ordered, 0);
    const amount    = round2(received * rate);

    if (cancelled > 0) { hasShort = true; totalCancelledQty += cancelled; }
    if (over > 0)      { hasOver  = true; totalOverQty      += over; }
    grandTotal += amount;

    if (received > 0 && !line.is_custom_item) {
      stockLines.push({ line, received, rate });
    }

    return {
      ...line,
      rate,
      amount,
      received_qty:        received,
      pending_qty:         0,
      cancelled_qty:       cancelled,
      over_qty:            over,
      item_grn_status:     getItemGRNStatus(received, ordered),
      item_receive_status: "completed",
    };
  });

  if (hasShort && !reason) {
    throw new Error("Some items arrived short — a reason is required to close the remaining quantity.");
  }

  return {
    mergedItems,
    stockLines,
    grandTotal: round2(grandTotal),
    hasShort, hasOver,
    totalOverQty, totalCancelledQty,
  };
};

/* ==================================================
   STAGE 2 — MANAGER/SM/OWNER FINALIZES WITH RATE
   The ONLY step that moves stock or money. Rate mandatory per received
   (non-custom) item — same discipline as ReceiveMaterial.js.
================================================== */
export const finalizeSitePurchaseGRN = async ({
  po_id,
  company_id,
  rates = {},                // { [lineKeyOf(line)]: rate }
  short_close_reason = "",   // mandatory when any line arrived short
  actual_freight = 0,        // optional, reference only — never in any total
  completed_by,
  completed_by_name = "",
}) => {
  if (!po_id)        throw new Error("po_id required");
  if (!company_id)   throw new Error("company_id required");
  if (!completed_by) throw new Error("completed_by required");

  const reason      = String(short_close_reason || "").trim();
  const freight     = Number(actual_freight || 0);
  if (!(freight >= 0)) throw new Error("Actual freight must be 0 or more");

  const poRef      = doc(db, "purchase_orders", po_id);
  // Receipt id reserved up front and saved on the PO with the claim, so if
  // finalize ever stops part-way, the PO itself shows which receipt to check.
  const receiptRef = doc(collection(db, "purchase_receipts"));

  // ── STEP 1: check + plan + claim, in ONE transaction ──
  // Every check and the whole plan run BEFORE the claim is written, so a
  // validation error (missing rate, missing reason) leaves the PO untouched.
  const { po, plan } = await runTransaction(db, async (tx) => {
    const snap = await tx.get(poRef);
    if (!snap.exists()) throw new Error("PO not found");
    const data = snap.data();

    if (data.company_id !== company_id) throw new Error("Unauthorized company access");
    if (data.purchase_mode !== "direct_site_purchase") {
      throw new Error("This PO is not a Direct Site Purchase");
    }
    if (data.grn_locked) throw new Error("GRN already completed for this PO");
    if (data.site_confirmation_status === "finalizing") {
      throw new Error("Finalization of this PO has already started — refresh the page.");
    }
    if (data.site_confirmation_status !== "site_confirmed") {
      throw new Error("Site Engineer has not confirmed receipt yet");
    }
    if (!data.site_id) throw new Error("PO has no site_id — cannot credit site stock");

    const built = buildFinalizePlan(data, rates, reason);

    tx.update(poRef, {
      site_confirmation_status: "finalizing",
      finalizing_by:            completed_by,
      finalizing_by_name:       completed_by_name,
      finalizing_started_at:    serverTimestamp(),
      finalizing_receipt_id:    receiptRef.id,
      updated_at:               serverTimestamp(),
    });

    return { po: data, plan: built };
  });

  // ── STEPS 2–7: writes. If anything fails from here on, the PO stays
  // "finalizing" on purpose — a retry could credit site stock twice. ──
  try {
    // receipt_type keeps its existing value "direct_site_purchase" (already on
    // every finalized receipt and bill row) — readers may filter on it. Short
    // delivery is shown by the separate short_close / short_closed fields.
    const receiptType = "direct_site_purchase";
    const receiptItems = plan.mergedItems.map(item => ({
      ...item,
      amount: round2(Number(item.received_qty || 0) * Number(item.rate || 0)),
    }));

    // ── STEP 2: purchase_receipts — Material Receive SOT. Same field names
    // as completePurchaseReceipt(), so GRNDetail.jsx shows it unchanged. ──
    await setDoc(receiptRef, {
      purchase_order_id:      po_id,
      company_id,
      po_number:              po.po_number   || "",
      vendor_id:              po.vendor_id   || "",
      vendor_name:            po.vendor_name || "",
      receipt_type:           receiptType,
      items:                  receiptItems,
      grand_total:            plan.grandTotal,
      actual_freight_amount:  freight,
      over_received:          plan.hasOver,
      over_qty:               plan.totalOverQty,
      short_close:            plan.hasShort,
      short_close_reason:     plan.hasShort ? reason : "",
      total_cancelled_qty:    plan.totalCancelledQty,
      // D-291 P3d-4: the vendor's bill is entered later (Awaiting Bills).
      bill_status:            plan.grandTotal > 0 ? "awaiting_bill" : "not_needed",
      is_direct_site_purchase: true,
      project_id:             po.project_id   || "",
      project_name:           po.project_name || "",
      site_id:                po.site_id      || "",
      site_name:              po.site_name    || "",
      site_confirmed_by:      po.site_confirmed_by      || "",
      site_confirmed_by_name: po.site_confirmed_by_name || "",
      completed_by,
      completed_by_name,
      created_at:             serverTimestamp(),
    });

    // ── STEP 3: site stock. Custom items are record-only — never credited
    // (Constitution §5 / CORE_RULES §6). Central inventory_summary is never
    // touched by this flow. ──
    for (const s of plan.stockLines) {
      await receiveGRNAtSite({
        company_id,
        site_id:        po.site_id,
        site_name:      po.site_name || "",
        item_id:        s.line.item_id,
        item_code:      s.line.item_code || "",
        item_name:      s.line.item_name,
        category:       s.line.item_category || s.line.category || "MATERIAL",
        qty:            s.received,
        unit:           s.line.unit || "nos",
        rate:           s.rate,
        reference_id:   po_id,
        reference_type: "direct_site_purchase_grn",
        remarks:        plan.hasShort
          ? `Direct Site Purchase — GRN at site (short close: ${reason})`
          : "Direct Site Purchase — GRN at site",
        project_id:     po.project_id || "",
        project_name:   po.project_name || "",
      });
    }

    // ── STEP 4: Material Control sync — non-blocking (D-279). ──
    if (po.project_id && plan.stockLines.length > 0) {
      try {
        await applyDirectSiteIssueToStages(
          company_id,
          po.project_id,
          plan.stockLines.map(s => ({ item_id: s.line.item_id, qty_issued: s.received }))
        );
      } catch (stageErr) {
        console.error("finalizeSitePurchaseGRN — Material Control sync failed (non-blocking):", stageErr);
      }
    }

    // ── STEP 5: PROJECT COST — allocation row (D-289 Step 0a).
    // Material bought straight to site never passes issueAgainstMR(), so this
    // row charges it to the project. It is an ALLOCATION row: the vendor's
    // purchase bill (entered later, D-291 P3d) is the company-level spend;
    // company totals count only one of them (isProjectAllocation(), D-289
    // Step 0b). Non-blocking. ──
    if (po.project_id && plan.grandTotal > 0) {
      try {
        await addDoc(collection(db, "financial_transactions"), {
          company_id,
          type:                "expense",
          category:            "material",
          amount:              plan.grandTotal,
          project_id:          po.project_id,
          project_name:        po.project_name || "",
          site_id:             po.site_id || "",
          site_name:           po.site_name || "",
          vendor_id:           po.vendor_id   || "",
          vendor_name:         po.vendor_name || "",
          purchase_order_id:   po_id,
          po_number:           po.po_number || "",
          purchase_receipt_id: receiptRef.id,
          reference_type:      "direct_site_purchase",
          reference_id:        receiptRef.id,
          description:         `Direct Site Purchase — ${po.po_number || po_id} — ${po.vendor_name || ""}`,
          created_by:          completed_by,
          created_by_name:     completed_by_name,
          created_at:          serverTimestamp(),
        });
      } catch (costErr) {
        console.error("finalizeSitePurchaseGRN — project cost write failed (non-blocking):", costErr);
      }
    }

    // (STEP 6 removed - D-291 P3d-4: the vendor payable comes from the
    // purchase bill, entered in Awaiting Bills.)

    // ── STEP 7: PO — merged items (nothing from creation is lost) + the
    // same short/over fields the normal GRN writes. ──
    const poStatusAfter = plan.hasShort ? "short_closed"
      : plan.hasOver ? "over_received" : "completed";

    await updateDoc(poRef, {
      items:                    plan.mergedItems,
      site_confirmation_status: "completed",
      grn_locked:               true,
      po_status:                poStatusAfter,
      receive_status:           "completed",
      received_items:           plan.stockLines.length,
      pending_items:            0,
      completion_percentage:    100,
      grand_total:              plan.grandTotal,
      purchase_receipt_id:      receiptRef.id,
      short_closed:             plan.hasShort,
      short_close_reason:       plan.hasShort ? reason : "",
      short_closed_by:          plan.hasShort ? completed_by      : "",
      short_closed_by_name:     plan.hasShort ? completed_by_name : "",
      short_closed_at:          plan.hasShort ? serverTimestamp()  : null,
      over_received:            plan.hasOver,
      over_qty:                 plan.totalOverQty,
      total_cancelled_qty:      plan.totalCancelledQty,
      ...(freight > 0 ? { actual_freight_amount: freight } : {}),
      completed_by,
      completed_by_name,
      completed_at:             serverTimestamp(),
      updated_at:               serverTimestamp(),
      updated_by:               completed_by,
    });

    return {
      success:     true,
      grand_total: plan.grandTotal,
      receipt_id:  receiptRef.id,
      po_status:   poStatusAfter,
      bill_status: plan.grandTotal > 0 ? "awaiting_bill" : "not_needed",
    };

  } catch (error) {
    console.error("finalizeSitePurchaseGRN stopped part-way:", error);
    throw new Error(
      `Finalization stopped part-way (${error.message}). Do NOT try again — this PO is marked ` +
      `"Finalizing" so it cannot be finalized twice. Ask admin to check its site stock first.`
    );
  }
};